import React, { useState } from 'react';
import { Modal, Button, Form, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

interface SendMessageModalProps {
  show: boolean;
  onHide: () => void;
  player: { id: string; name: string; contact: string; isOnline: boolean };
  onSend: (playerId: string, message: string, senderLatitude: number, senderLongitude: number, searcherUniqueId: string | null, gameDate: string, gameTime: string) => void; // Updated prop for sending message
  senderLatitude: number;
  senderLongitude: number;
  searcherUniqueId: string | null;
  gameAddress: string; // New prop for game address
  gameDate: string; // New prop for game date
  gameTime: string; // New prop for game time
  recipientName: string; // New prop for the recipient's name
  recipientIsSearcher?: boolean;
}

const SendMessageModal: React.FC<SendMessageModalProps> = ({ show, onHide, player, onSend, senderLatitude, senderLongitude, searcherUniqueId, gameAddress, gameDate, gameTime, recipientName, recipientIsSearcher }) => {
  const { t } = useTranslation();
  const defaultMessage = recipientIsSearcher ? 'defaultMessageForSearcher' : 'defaultMessage';
  const [messageContent, setMessageContent] = useState<string>(t(defaultMessage, { user: recipientName }));

  // Update message content if player changes (e.g., modal is reused for different players)
  React.useEffect(() => {
    setMessageContent(t(defaultMessage, { user: recipientName }));
  }, [recipientName, t]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Optionally, show a toast notification
  };

  const getContactLink = (contact: string) => {
    if (contact.includes('@')) {
      return <a href={`mailto:${contact}`}>{contact}</a>;
    } else if (/^\+?[0-9\s-()]{7,20}$/.test(contact)) { // Basic phone number regex
      return <a href={`tel:${contact.replace(/\s/g, '')}`}>{contact}</a>;
    } else {
      return contact;
    }
  };

  const handleSendMessage = () => {
    if (!messageContent) {
      alert(t('fillAllFields')); // Simple alert for now
      return;
    }
    onSend(player.id, messageContent, senderLatitude, senderLongitude, searcherUniqueId, gameDate, gameTime);
    onHide(); // Close modal after sending
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('sendMessageTo', { playerName: recipientName })}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!recipientIsSearcher && (<>
          <p>{t('playerContact')}: <b>{getContactLink(player.contact)}</b></p>
          <InputGroup className="mb-3">
            <Form.Control
              type="text"
              value={player.contact}
              readOnly
            />
            <Button variant="outline-secondary" onClick={() => copyToClipboard(player.contact)}>
              {t('copyContact')}
            </Button>
          </InputGroup>
        </>
        )}

        <p>{t('suggestedMessage')}:</p>
        <InputGroup className="mb-3">
          <Form.Control
            as="textarea"
            rows={3}
            value={messageContent}
            onChange={(e) => setMessageContent(e.target.value)}
          />
          <Button variant="outline-secondary" onClick={() => copyToClipboard(messageContent)}>
            {t('copyMessage')}
          </Button>
        </InputGroup>

        {!recipientIsSearcher && <p>{t('contactInstructions')}</p>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('close')}
        </Button>
        <Button variant="primary" onClick={handleSendMessage}>
          {t('send')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SendMessageModal;
